import React, {useEffect} from 'react';
import {isMobileSafari, isSafari} from "react-device-detect";
import {store} from "../store/Store";

const noopMusicStub = {
    stop: () => {},
    play: () => {},
    pause: () => {},
    mute: () => {},
    unmute: () => {},
    setQueue: () => Promise.resolve(),
    clearQueue: () => {},
    skipToNextItem: () => {},
    skipToPreviousItem: () => {},
    isAuthorized: false,
    addEventListener: () => {},
    removeEventListener: () => {},
    api: {music: () => Promise.resolve({data: {results: {}}})},
    authorize: () => Promise.resolve(null),
}

export const getMusicInstance = () => {
    try {
        return window.MusicKit?.getInstance?.() || noopMusicStub
    } catch (e) {
        return noopMusicStub
    }
}

export const MusicKitInit = () => {
    //console.log('MusicKit initializing')
    if (!isAutoplayEnabled()) {
        document.body.addEventListener('touchstart', function () {
            /**
             * Important hack for Safari
             */
            let empty = new Audio("audio/empty.mp3");
            empty.play().then(_ => {
                empty.pause();
                empty.currentTime = 0;
            });
        }, false);
    }
}

export const isAutoplayEnabled = () => {
    return !(isSafari || isMobileSafari);
}

export const clearQueue = () => {
    const music = getMusicInstance();
    music.clearQueue();
}

export const searchSong = async (artistTitle) => {
    if (artistTitle) {
        try {
            const music = getMusicInstance();
            const lookup = artistTitle.replace(/ *\([^)]*\) */g, "");//removing parenthesis and content
            const queryParameters = {term: lookup, types: ['songs'], l: 'en-us'};
            const response = await music.api.music('/v1/catalog/{{storefrontId}}/search', queryParameters);

            if (response.data.results && response.data.results.songs && response.data.results.songs.data) {
                return response.data.results.songs.data[0];
            } else {
                // //If not found at the first place, retry after cleaning artistTitle field
                // let cleanedArtistTitle = artistTitle.replace(/ *\([^)]*\) */g, "");//removing parenthesis and content
                // return searchSong(cleanedArtistTitle);
                return false;
            }
        } catch (error) {
            errorHandler(error, 'searchSong');
        }
    }
    return false;
}


export const getPlaylist = async () => {
    const music = getMusicInstance();
    try {
        const state = store.getState();

        if (state.musicKit.playlist) {
            return await Promise.resolve(state.musicKit.playlist);
        } else {
            return music.authorize().then(async () => {
                const queryParameters = {l: 'en-us'};
                const response = await music.api.music('/v1/me/library/playlists', queryParameters);
                const responseData = response.data.data;

                const playlist = responseData.find(obj => obj.attributes.name === process.env.REACT_APP_APPLE_PLAYLIST_NAME);

                if (!playlist) {
                    return await createPlaylist();
                } else {
                    return await Promise.resolve(playlist);
                }
            });
        }


    } catch (error) {
        errorHandler(error, 'getPlaylist');
    }
}


export const getSongsFromPlaylist = async () => {
    const music = getMusicInstance();

    if (music.isAuthorized) {
        return getPlaylist().then(async playlist => {
                try {
                    const response = await fetch(`https://api.music.apple.com/v1/me/library/playlists/${playlist.id}/tracks`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ` + music.developerToken,
                            'Music-User-Token': music.musicUserToken
                        }
                    });

                    if (response.status === 404) {
                        return [];
                    } else {
                        const responseBody = await response.json();
                        if (responseBody.data) {
                            return responseBody.data;
                        } else {
                            errorHandler('Fail to get songs from playlist', 'getSongsFromPlaylist');
                        }
                    }
                } catch (error) {
                    errorHandler(error, 'getSongsFromPlaylist');
                }
                return [];
            }
        );
    }
    return [];
}

export const addToPlaylist = async (songLookup) => {
    const music = getMusicInstance();

    if (music.isAuthorized) {
        return searchSong(songLookup).then(async song => {
            return getPlaylist().then(async playlist => {
                const response = await fetch(`https://api.music.apple.com/v1/me/library/playlists/${playlist.id}/tracks`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ` + music.developerToken,
                        'Music-User-Token': music.musicUserToken,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        data: [
                            {
                                id: song.id,
                                type: 'songs'
                            }
                        ]
                    })
                });

                if (response.status === 204) {
                    console.log("Song '" + songLookup + "' added to playlist successfully");
                    return Promise.resolve(true);
                } else {
                    errorHandler("Failed to add '" + songLookup + "' to playlist", 'addToPlaylist');
                    return Promise.resolve(false);
                }
            });
        });
    }

    return false;

}

export const removePlaylist = async () => {
    const music = getMusicInstance();

    if (music.isAuthorized) {
        return getPlaylist().then(async playlist => {
            const response = await fetch(`https://api.music.apple.com/v1/me/library/playlists/${playlist.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ` + music.developerToken,
                    'Music-User-Token': music.musicUserToken
                }
            });
            console.log('removePlaylist response', response);

            if (response.status === 204) {
                console.log('Playlist removed successfully');
                return Promise.resolve(true);
            } else {
                errorHandler('Failed to remove playlist', 'removePlaylist');
                return Promise.resolve(false);
            }
        });
    }

    return false;
}

/**
 * Removing from playlist is not supported by Apple Web API
 *
 * @param song
 * @returns {Promise<*|boolean|boolean>}
 */
export const removeFromPlaylist = async (song) => {
    const music = getMusicInstance();

    if (music.isAuthorized) {
        return searchSong(song.artist_title).then(song => {

            getPlaylist().then(async playlist => {
                const response = await fetch(`https://api.music.apple.com/v1/me/library/playlists/${playlist.id}/tracks/${song.id}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ` + music.developerToken,
                        'Music-User-Token': music.musicUserToken
                    }
                });
                console.log('removeFromPlaylist response', response);

                if (response.status === 204) {
                    console.log('Song removed from playlist successfully');
                    return Promise.resolve(true);
                } else {
                    errorHandler('Failed to remove song from playlist', 'removeFromPlaylist');
                    return Promise.resolve(false);
                }
            });
        });
    }
    return false;
}


export const createPlaylist = async () => {
    const music = getMusicInstance();

    if (music.isAuthorized) {
        const response = await fetch('https://api.music.apple.com/v1/me/library/playlists', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ` + music.developerToken,
                'Music-User-Token': music.musicUserToken,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                attributes: {
                    name: process.env.REACT_APP_APPLE_PLAYLIST_NAME
                }
            })
        });

        if (response.status === 201) {
            console.log('Playlist created successfully');
            const responseBody = await response.json();
            const playlist = responseBody.data[0];
            return Promise.resolve(playlist);
        } else {
            errorHandler('Failed to create playlist', 'createPlaylist');
            return Promise.resolve(false);
        }
    }
    return false;
};

const errorHandler = (msg, functionName) => {
    try {
        console.error('MusicKit.js Error :: ' + msg + ' in ' + functionName);
    } catch (err) {
        console.error('MusicKit.js Error :: ' + err);
    }
}

const LIKE_PLAYLIST_NAME = 'Discovered by AbTunes.com';
const LIKE_PLAYLIST_KEY = 'abtunes_like_playlist_id';

const appleFetch = (music, path, options = {}) => fetch('https://api.music.apple.com' + path, {
    ...options,
    headers: {
        'Authorization': 'Bearer ' + music.developerToken,
        'Music-User-Token': music.musicUserToken,
        'Content-Type': 'application/json',
    },
});

/**
 * Find (paging the whole library) or create the likes playlist; id cached
 * locally. With no firstAppleId it only looks, never creates.
 */
const ensureLikePlaylist = async (music, firstAppleId) => {
    let cached = null;
    try {
        cached = localStorage.getItem(LIKE_PLAYLIST_KEY);
    } catch (e) { /* storage blocked */ }
    if (cached) {
        return {id: cached, created: false};
    }
    let path = '/v1/me/library/playlists?limit=100';
    while (path) {
        const res = await appleFetch(music, path);
        if (!res.ok) {
            break;
        }
        const body = await res.json();
        const found = (body.data || []).find((p) => p.attributes?.name === LIKE_PLAYLIST_NAME);
        if (found) {
            try { localStorage.setItem(LIKE_PLAYLIST_KEY, found.id); } catch (e) { /* noop */ }
            return {id: found.id, created: false};
        }
        path = body.next || null;
    }
    if (!firstAppleId) {
        return {id: null, created: false};
    }
    const res = await appleFetch(music, '/v1/me/library/playlists', {
        method: 'POST',
        body: JSON.stringify({
            attributes: {name: LIKE_PLAYLIST_NAME, description: 'Songs you liked while discovering on abtunes.com'},
            relationships: {tracks: {data: [{id: String(firstAppleId), type: 'songs'}]}},
        }),
    });
    if (res.status !== 201) {
        throw new Error('create playlist: ' + res.status);
    }
    const id = (await res.json()).data[0].id;
    try { localStorage.setItem(LIKE_PLAYLIST_KEY, id); } catch (e) { /* noop */ }
    return {id, created: true};
};

/**
 * Like = add the exact catalog track to the user's Apple Music playlist
 * "Discovered by AbTunes.com" (created on first like). Apple's API can't
 * remove tracks from library playlists, so a like is one-way.
 */
export const likeOnApple = async (appleId) => {
    const music = getMusicInstance();
    if (!appleId) {
        return false;
    }
    if (!music.isAuthorized) {
        await music.authorize();
    }
    const {id, created} = await ensureLikePlaylist(music, appleId);
    if (created) {
        return true;
    }
    const res = await appleFetch(music, `/v1/me/library/playlists/${id}/tracks`, {
        method: 'POST',
        body: JSON.stringify({data: [{id: String(appleId), type: 'songs'}]}),
    });
    if (res.status === 404) {
        // Playlist deleted in Apple Music since we cached it: start over.
        try { localStorage.removeItem(LIKE_PLAYLIST_KEY); } catch (e) { /* noop */ }
        return likeOnApple(appleId);
    }
    return res.ok;
};

/**
 * The likes playlist as read from Apple Music (the source of truth, so it
 * reflects edits made in the Music app). null = Apple Music not connected.
 */
export const getLikedTracks = async () => {
    const music = getMusicInstance();
    if (!music.isAuthorized) {
        return null;
    }
    let {id} = await ensureLikePlaylist(music, null);
    if (!id) {
        return {playlistId: null, tracks: []};
    }
    const tracks = [];
    let path = `/v1/me/library/playlists/${id}/tracks?limit=100`;
    while (path) {
        const res = await appleFetch(music, path);
        if (res.status === 404) {
            if (tracks.length === 0 && path.includes('limit=100') && !path.includes('offset')) {
                // Either empty, or the cached playlist was deleted in Apple Music.
                const probe = await appleFetch(music, `/v1/me/library/playlists/${id}`);
                if (probe.status === 404) {
                    try { localStorage.removeItem(LIKE_PLAYLIST_KEY); } catch (e) { /* noop */ }
                    return {playlistId: null, tracks: []};
                }
            }
            break;
        }
        if (!res.ok) {
            break;
        }
        const body = await res.json();
        (body.data || []).forEach((t) => {
            const a = t.attributes || {};
            tracks.push({
                apple_id: a.playParams?.catalogId || null,
                title: a.name,
                artist: a.artistName,
                album: a.albumName,
                cover: a.artwork?.url ? a.artwork.url.replace('{w}', '200').replace('{h}', '200') : null,
            });
        });
        path = body.next ? body.next + (body.next.includes('limit=') ? '' : '&limit=100') : null;
    }
    return {playlistId: id, tracks};
};

export const likePlaylistUrl = (playlistId) => `https://music.apple.com/library/playlist/${playlistId}`;
